import React, { FC } from 'react';
import styles from './Experience.module.scss';

type ExperienceItem = {
  since: string;
  until?: string;
  company: string;
  role: string;
  description: string;
  highlights: string[];
  skills: string;
};

const experiences: ExperienceItem[] = [
  {
    since: 'March 2024',
    company: 'FARO Technologies',
    role: 'Senior Software Engineer',
    description:
      'Senior-level developer within an international engineering team, developing and optimizing enterprise web applications that handle high-volume client data, with a strong focus on frontend architecture, performance and maintainability.',
    highlights: [
      'Independently researched and introduced AI Agents and custom Skills into the product ecosystem',
      'Maintained strict runtime schema validation with Zod across the client-side API layer',
      'Investigated and resolved performance issues from large enterprise data payloads',
      'Refactored legacy React/TypeScript components toward modern standards',
      "Contributed to the evolution of the company's internal Design System",
    ],
    skills:
      'React, TypeScript, Node.js, Redux Toolkit, REST APIs, Zod, Microfrontends, Material-UI, Vitest, Azure DevOps, Git, CI/CD',
  },
  {
    since: 'October 2021',
    until: 'December 2023',
    company: 'Talkdesk',
    role: 'Frontend Developer',
    description:
      'Primary Frontend Engineer within a flat, collaborative team, taking full ownership of the user interface architecture while working alongside backend-focused peers.',
    highlights: [
      'Helped build a real-time agent workspace, replacing multi-tab workflows with instant data access',
      'Reusable component development for the company-wide design system',
      'API integration and performance optimization',
      'CI/CD collaboration',
    ],
    skills: 'React, TypeScript, Redux Toolkit, GraphQL, OpenAPI, Jest, Storybook',
  },
  {
    since: 'December 2018',
    until: 'October 2021',
    company: 'Critical TechWorks',
    role: 'Frontend Developer',
    description:
      'Worked on a data-management platform for BMW, consolidating several department-specific applications into a single, unified tool with role-based access to relevant data and metrics.',
    highlights: [
      'Close collaboration with design',
      'Cross-browser support including legacy Internet Explorer',
      'D3.js data visualization metrics',
      'Custom reusable components and responsive behavior',
    ],
    skills: 'Angular, TypeScript, Jest, Cypress, D3.js, SCSS, Version Control',
  },
  {
    since: 'August 2017',
    until: 'November 2018',
    company: 'China Systems',
    role: 'Frontend Developer',
    description:
      'At China Systems, as a Junior Frontend Developer, I worked on client-side development for two home banking platforms using vanilla JavaScript, within a proprietary internal tooling system (no external testing frameworks). This was my first contact with waterfall methodology.',
    highlights: [
      'Cross Browsing in Internet Explorer 9+',
      'Responsive website',
      'Polyfill to provide compatibility with older browsers',
      'Version Control',
    ],
    skills: 'JavaScript, jQuery, JSP, XML, CSS, HTML, IBM DB2',
  },
  {
    since: 'September 2016',
    until: 'August 2017',
    company: 'Bee Engineering',
    role: 'IT Consultant',
    description:
      'Started by developing an internal AngularJS project and later joined China Systems as a consultant before becoming permanent.',
    highlights: ['Built internal project from scratch', 'Consulting for enterprise frontend delivery'],
    skills: 'AngularJS, HTML5, CSS, JavaScript, MySQL, Git',
  },
];

const Experience: FC = () => {
  return (
    <section id="experience">
      <div className={styles.container}>
        <div className={styles.row}>
          <h2 className={styles.header}>Experience</h2>
          <ul className={styles.timeline}>
            {experiences.map(experience => (
              <li key={`${experience.company}-${experience.since}`} className={styles['timeline-item']}>
                <div className={styles['timeline-meta']}>
                  <p className={styles['timeline-info']}>
                    {experience.since}
                    {experience.until ? ` - ${experience.until}` : ''}
                  </p>
                </div>

                <div className={styles['timeline-content']}>
                  <h3 className={styles['timeline-title']}>{experience.company}</h3>
                  <p className={styles['timeline-subTitle']}>{experience.role}</p>
                  <p>{experience.description}</p>
                  <ul className={styles.responsibilities}>
                    {experience.highlights.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <p className={styles.skills}>Skills: {experience.skills}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Experience;
